/**
 * Checks if the target of a keyboard event is an editable element.
 *
 * Determines if the event target is a text input, textarea, or contenteditable element.
 * Useful for preventing keyboard shortcuts when user is typing in form fields.
 *
 * @param e - The keyboard event to check
 * @returns True if the target is editable, false otherwise
 *
 * @example
 * ```typescript
 * document.addEventListener('keydown', (e) => {
 *   if (!isTargetEditable(e) && e.key === 'Enter') {
 *     // Only trigger shortcut if user isn't typing in a form field
 *     handleEnterShortcut()
 *   }
 * })
 * ```
 */
export const isTargetEditable = (e: KeyboardEvent): boolean => {
  const target = e.target as any

  if (!target) return false

  // Check for INPUT elements with editable types
  if (target.tagName === 'INPUT') {
    const editableTypes = [
      'text',
      'search',
      'url',
      'tel',
      'number',
      'email',
      'password',
    ]
    return editableTypes.includes(target.type?.toLowerCase())
  }

  // Check for TEXTAREA elements
  if (target.tagName === 'TEXTAREA') {
    return true
  }

  // Check for contenteditable elements using DOM property
  if (target.isContentEditable === true) {
    return true
  }

  // Fallback: check contenteditable attribute directly
  if (
    target.getAttribute &&
    target.getAttribute('contenteditable') === 'true'
  ) {
    return true
  }

  return false
}

/**
 * Mapping of key names to their corresponding key codes for browser compatibility.
 *
 * Used to support older browsers that don't have the modern `key` property
 * and still rely on `keyCode`.
 */
const keyMapping: {
  [key: string]: number
} = {
  Escape: 27,
  ' ': 32,
  ArrowLeft: 37,
  ArrowUp: 38,
  ArrowRight: 39,
  ArrowDown: 40,
  Enter: 13,
  Tab: 9,
  Delete: 8,
}

/**
 * Cross-browser compatible key comparison for keyboard events.
 *
 * Compares both the modern `key` property and legacy `keyCode` property
 * to ensure compatibility with older browsers that don't support the `key` property.
 *
 * @param e - The keyboard event (React or native)
 * @param key - The key name to compare against (e.g., 'Enter', 'Escape')
 * @returns True if the pressed key matches the specified key
 *
 * @example
 * ```typescript
 * const handleKeyDown = (e: KeyboardEvent) => {
 *   if (keyEqualTo(e, 'Enter')) {
 *     // Handle Enter key press
 *   }
 *   if (keyEqualTo(e, 'Escape')) {
 *     // Handle Escape key press
 *   }
 * }
 * ```
 */
export const keyEqualTo = (
  e: React.KeyboardEvent<HTMLElement> | KeyboardEvent,
  key: string,
): boolean => e.key === key || e.keyCode === keyMapping[key]
